import { useMemo, useState } from "react";
import CodeMirror, { EditorView } from "@uiw/react-codemirror";
import type { Extension } from "@uiw/react-codemirror";
import { vscodeDark } from "@uiw/codemirror-theme-vscode";
import classNames from "classnames";

import { getLanguageExtension } from "../../../../shared/utils/codeLanguages";
import { PROJECT_MESSAGES } from "../../messages";
import type { RunOutput, WorkspaceType } from "../../projectTypes";
import type { FileMap } from "../../utils/project";
import RunResult from "./RunResult";
import WebPreview from "./WebPreview";

const RESULT_TAB = "__result__"; // Not a slot name, so it can't clash with an editor

const editorTheme = EditorView.theme({
    "&": { fontSize: "14px" },
    ".cm-content": { padding: "16px 0" }
});

export interface WorkspaceSlot {
    slot: string; // "main", "html", "css", "js", "sql"
    label: string;
    language: string;
    starterCode: string;
}

interface SlotEditorProps {
    language: string;
    value: string;
    onChange: (code: string) => void;
}

const SlotEditor = ({ language, value, onChange }: SlotEditorProps) => {
    const extensions = useMemo<Extension[]>(() => {
        const extension = getLanguageExtension(language);
        return extension ? [editorTheme, extension] : [editorTheme];
    }, [language]);

    return (
        <div className="h-[480px]">
            <CodeMirror
                value={value}
                height="480px"
                theme={vscodeDark}
                extensions={extensions}
                onChange={onChange}
                basicSetup={{
                    lineNumbers: true,
                    foldGutter: true,
                    highlightActiveLine: true,
                    autocompletion: true
                }}
            />
        </div>
    );
};

interface ProjectWorkspaceProps {
    type: WorkspaceType;
    slots: WorkspaceSlot[]; // The editors of the project: one tab each
    files: FileMap;
    onFileChange: (slot: string, code: string) => void;
    stdin: string; // Only programs have input
    onStdinChange: (stdin: string) => void;
    output: RunOutput;
    running: boolean;
    onRun: () => void;
    resultTabRequest: number; // Increases on every run, which switches to the result tab
}

// The learner's workspace. Its tabs depend on the project: a program has one editor, a web project
// has HTML, CSS and JS editors (as the template says), SQL has one editor. All have a result tab.
const ProjectWorkspace = ({
                              type,
                              slots,
                              files,
                              onFileChange,
                              stdin,
                              onStdinChange,
                              output,
                              running,
                              onRun,
                              resultTabRequest
                          }: ProjectWorkspaceProps) => {
    const [selectedTab, setSelectedTab] = useState<string>(slots[0]?.slot ?? RESULT_TAB);
    const [lastRequest, setLastRequest] = useState(resultTabRequest);

    // A new run opens the result tab (adjusting state while rendering, no effect needed)
    if (lastRequest !== resultTabRequest) {
        setLastRequest(resultTabRequest);
        setSelectedTab(RESULT_TAB);
    }

    const tabs = [
        // A single editor is just "Code"; several editors keep their own names
        ...slots.map(({ slot, label }) => ({ key: slot, label: slots.length === 1 ? PROJECT_MESSAGES.tabCode : label })),
        { key: RESULT_TAB, label: type === "web" ? PROJECT_MESSAGES.preview : PROJECT_MESSAGES.tabResult }
    ];

    // The project's editors can change (another step, another template): a missing tab falls back to the first
    const activeTab = tabs.some((tab) => tab.key === selectedTab) ? selectedTab : tabs[0].key;
    const activeSlot = slots.find((slot) => slot.slot === activeTab);

    return (
        <div className="min-w-0">
            <div className="rounded-2xl border border-gray-200 overflow-hidden bg-white">
                <div role="tablist" className="flex border-b border-gray-200 overflow-x-auto">
                    {tabs.map(({ key, label }) => (
                        <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === key}
                            onClick={() => setSelectedTab(key)}
                            className={classNames(
                                "px-6 py-3 text-sm font-medium transition whitespace-nowrap",
                                activeTab === key
                                    ? "border-b-2 border-black text-black"
                                    : "text-gray-500 hover:text-gray-800"
                            )}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {activeSlot && (
                    <div dir="ltr">
                        {/* key: every editor keeps its own state (cursor, undo) while the tabs switch */}
                        <SlotEditor
                            key={activeSlot.slot}
                            language={activeSlot.language}
                            value={files[activeSlot.slot] ?? activeSlot.starterCode ?? ""}
                            onChange={(code) => onFileChange(activeSlot.slot, code)}
                        />

                        {type === "program" && (
                            <div className="border-t border-gray-200 p-4" dir="rtl">
                                <label htmlFor="program-input" className="block text-sm font-medium text-gray-700 mb-2">
                                    {PROJECT_MESSAGES.programInput}
                                    <span className="text-xs text-gray-400 me-2">{PROJECT_MESSAGES.inputHint}</span>
                                </label>

                                <textarea
                                    id="program-input"
                                    dir="ltr"
                                    rows={4}
                                    value={stdin}
                                    onChange={(event) => onStdinChange(event.target.value)}
                                    placeholder={PROJECT_MESSAGES.inputPlaceholder}
                                    spellCheck={false}
                                    className="w-full rounded-xl border border-gray-200 bg-gray-50 p-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-black/10 resize-y"
                                />
                            </div>
                        )}
                    </div>
                )}

                {activeTab === RESULT_TAB && (
                    <div dir="ltr" className="min-h-[480px] max-h-[600px] bg-gray-950 text-white p-5 font-mono overflow-auto">
                        <div className="text-gray-400 text-sm mb-3 w-full p-2">{PROJECT_MESSAGES.resultTitle}</div>

                        {/* A web page is shown live, without running anything. Run only checks it. */}
                        {type === "web" && <WebPreview files={files} />}

                        <RunResult
                            type={type}
                            output={output}
                            running={running}
                            hasRun={resultTabRequest > 0}
                        />
                    </div>
                )}
            </div>

            <div className="flex justify-end mt-4">
                <button
                    type="button"
                    disabled={running}
                    onClick={onRun}
                    className="rounded-xl bg-black text-white px-6 py-3 font-medium hover:bg-gray-800 transition disabled:opacity-50"
                >
                    {running ? PROJECT_MESSAGES.running : PROJECT_MESSAGES.runCode}
                </button>
            </div>
        </div>
    );
};

export default ProjectWorkspace;